import React, { useEffect, useRef, useState } from "react";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { useExecutiveSummary, useKpis, useMonthlyTrends, useTrendChart } from "../hooks/useDashboardData";
import { API_BASE_URL } from "../api/config";

interface Props {
  filters: any;
}

// 12 perceptually distinct colors — evenly spaced hues, no repeats
const CHART_PALETTE = [
  "#2563EB",  // blue
  "#DC2626",  // red
  "#16A34A",  // green
  "#D97706",  // amber
  "#7C3AED",  // violet
  "#0891B2",  // cyan
  "#DB2777",  // pink
  "#84CC16",  // lime
  "#EA580C",  // orange
  "#0D9488",  // teal
  "#9333EA",  // purple
  "#B45309",  // brown
];


async function callChatApi(message: string): Promise<string> {
  const res = await fetch(`${API_BASE_URL}/api/chat/message`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ message, conversationId: null }),
  });
  if (!res.ok) throw new Error("Chat API request failed");
  const json = await res.json();
  return json.reply ?? "";
}

const ExecutiveSummary: React.FC<Props> = ({ filters }) => {
  const { data, isLoading, error } = useExecutiveSummary(filters);
  const { data: globalKpis, isFetching: kpiFetching } = useKpis(filters);
  const { data: monthlyData, isFetching: monthlyFetching } = useMonthlyTrends(filters);
  const { data: quarterlyData, isFetching: quarterlyFetching } = useTrendChart(filters);
  const mainRef = useRef<HTMLDivElement>(null);
  const [exporting, setExporting] = useState(false);
  const [trendView, setTrendView] = useState<"monthly" | "quarterly">("quarterly");
  const chartRefreshing = trendView === "quarterly" ? quarterlyFetching : monthlyFetching;

  // Compute variance using ACCOUNT_CATEGORY from backend (not name heuristic):
  //   REVENUE: actual - budget (positive = surplus/favorable)
  //   EXPENSE: budget - actual (positive = saving/favorable)
  const computeVariance = (
    accounts: string[],
    actualsRows: any[],
    budgetRows: any[],
    xKey: string,
    categoryMap?: Record<string, string>,
  ) =>
    actualsRows.map((actualRow: any, i: number) => {
      const budgetRow = budgetRows[i] ?? {};
      const row: any = { [xKey]: actualRow[xKey] };
      accounts.forEach((acc) => {
        const actual = actualRow[acc] ?? 0;
        const budget = budgetRow[acc] ?? 0;
        const isRevenue = (categoryMap?.[acc] ?? "EXPENSE") === "REVENUE";
        row[acc] = parseFloat((isRevenue ? actual - budget : budget - actual).toFixed(2));
      });
      return row;
    });

  const quarterlyVarianceData = quarterlyData?.accounts?.length
    ? computeVariance(quarterlyData.accounts, quarterlyData.actualsChart.data, quarterlyData.budgetChart.data, "quarter", quarterlyData.accountCategoryMap)
    : [];

  const monthlyVarianceData = monthlyData?.accounts?.length && monthlyData?.budgetData
    ? computeVariance(monthlyData.accounts, monthlyData.data, monthlyData.budgetData, "monthLabel", monthlyData.accountCategoryMap)
    : [];
  const [aiCommentary, setAiCommentary] = useState<string | null>(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);

  const isFirstMount = useRef(true);
  const prevFiltersRef = useRef<any>(null);
  // Set when filters change; cleared once KPI fetch completes and AI fires
  const pendingAiRef = useRef(false);

  // Mark pending when filters change
  useEffect(() => {
    if (isFirstMount.current) {
      isFirstMount.current = false;
      prevFiltersRef.current = filters;
      return;
    }
    if (!data) return;
    if (JSON.stringify(prevFiltersRef.current) !== JSON.stringify(filters)) {
      prevFiltersRef.current = filters;
      pendingAiRef.current = true;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, data]);

  // Fire AI generation only after the KPI fetch for the new filters has resolved
  useEffect(() => {
    if (pendingAiRef.current && !kpiFetching && globalKpis) {
      pendingAiRef.current = false;
      handleGenerateAI();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kpiFetching, globalKpis]);

  const handleExportPdf = async () => {
    if (!mainRef.current || exporting) return;
    setExporting(true);
    try {
      const canvas = await html2canvas(mainRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
      });
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "px",
        format: [canvas.width / 2, canvas.height / 2],
      });
      pdf.addImage(imgData, "PNG", 0, 0, canvas.width / 2, canvas.height / 2);
      pdf.save(`executive-summary-FY${filters?.fiscalYear ?? "report"}.pdf`);
    } finally {
      setExporting(false);
    }
  };

  const handleGenerateAI = async () => {
    if (!data) return;
    setAiLoading(true);
    setAiError(null);
    try {
      const kpiList = (globalKpis ?? data.kpis ?? [])
        .map((k: any) => `${k.label}: ${k.value} (${k.change} vs budget)`)
        .join(", ");

      const bridgeItems = (data.bridge?.data ?? [])
        .map((b: any) => `${b.name}: $${b.value}M`)
        .join(", ");

      const prompt = `You are a financial analyst generating executive-ready commentary for ABB financial data. Respond ONLY with valid HTML. Do not use markdown. Do not include code fences. Do not include any text outside HTML tags.

Use the filtered source dataset as the only source of truth. Do not fabricate numbers, drivers, or business explanations.

Your task:
1. Apply the filters below to the source dataset.
2. Calculate Budget, Actuals, Variance $, and Variance % from the filtered data.
3. Interpret the variance based on the account category.
4. Generate concise executive commentary in HTML.

Filters applied:
- Fiscal Year: FY${filters?.fiscalYear ?? "N/A"}
- Budget Version: ${filters?.budgetVersion ?? "Latest"}
- Account Category: ${filters?.accountCategory ?? "All"}
- GL Account: ${filters?.glAccount ?? "All"}
- Cost Center: ${filters?.costCenter ?? "All CCs"}

Filtered dataset results:
Key metrics: ${kpiList}
Performance bridge: ${bridgeItems}

Calculation rules:
- Total Budget = sum of Budget values for the filtered data
- Total Actuals = sum of Actual values for the filtered data
- Variance Amount = Total Actuals - Total Budget for revenue; Total Actuals - Total Budget for expense
- Variance Percent: if Total Budget ≠ 0: ((Total Actuals - Total Budget) / Total Budget) * 100; if Total Budget = 0: return "N/A"

Variance interpretation rules:
For REVENUE — Variance > 0 = favorable; Variance < 0 = unfavorable
For EXPENSE — Variance > 0 = unfavorable (overspend); Variance < 0 = favorable (cost savings)
For PROFIT/MARGIN/INCOME — Variance > 0 = favorable; Variance < 0 = unfavorable
If account category is unknown or mixed — do not label the variance as favorable or unfavorable unless clearly supported.

Bridge analysis rules:
- Summarize the most important bridge contributors present in the data
- Do not invent bridge line items not present in the data

Narrative instructions:
- Clearly state whether the result is favorable or unfavorable based on the category logic above
- Quantify the result using exact values from the dataset
- Focus on magnitude of variance and key financial signals
- Do not speculate on root causes unless directly supported by the dataset

Formatting requirements — output exactly:
1. One executive summary paragraph <p>...</p> (max 35 words)
2. One bullet list <ul><li>...</li></ul> with 3 to 5 key insights
3. One forward-looking recommendation paragraph <p>...</p>

Formatting style:
- <strong style="color:#165697"> for favorable or positive items
- <strong style="color:#dd3c57"> for unfavorable or negative items
- Format currency in millions (e.g. $47.1M); format percentages to one decimal place (e.g. -85.4%)

Do not output JSON, markdown, methodology explanations, or anything outside HTML tags.`;

      const reply = await callChatApi(prompt);
      setAiCommentary(reply);
    } catch (err: any) {
      setAiError(err.message ?? "Failed to generate commentary");
    } finally {
      setAiLoading(false);
    }
  };

  if (isLoading && !data) {
    return (
      <div className="es-page">
        <div className="es-loading">
          <div className="es-loading-spinner" />
          Loading Executive Summary…
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="es-page">
        <div className="es-error">Unable to load executive summary data.</div>
      </div>
    );
  }

  return (
    <div className="es-page">
      {/* ── Main Content ──────────────────────────────────────── */}
      <main className="es-main">
        <div className="es-main-inner" ref={mainRef}>
          {/* Page header */}
          <div className="es-main-header">
            <h1 className="es-page-title">Executive Preview Panel</h1>
            <p className="es-page-subtitle">
              Real-time snapshot of the curated data narrative before distribution.
            </p>
          </div>

          {/* KPI row */}
          <div className="es-kpi-row" style={{ opacity: kpiFetching ? 0.6 : 1, transition: "opacity 0.2s" }}>
            {(globalKpis ?? data.kpis).map((kpi: any) => (
              <div key={kpi.label} className="es-kpi-card">
                <div className="es-kpi-label">{kpi.label}</div>
                <div className="es-kpi-bottom-row">
                  <span className="es-kpi-value">{kpi.value}</span>
                  <span className={`es-kpi-badge es-kpi-badge--${kpi.changeType}`}>
                    {kpi.trend === "up" ? "↑" : "↓"} {kpi.change}
                  </span>
                </div>
                <div className="es-kpi-comparison">{kpi.comparison}</div>
              </div>
            ))}
          </div>

          {/* ── Performance Trends (Quarterly / Monthly toggle) ── */}
          <div className="es-bridge-section">
            <div className="es-bridge-header">
              <div className="es-bridge-title-row">
                <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
                  <path d="M2 12L5 7l3 4 3-7 3 3" stroke="#165697" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <span className="es-bridge-title">Performance Variance Trends by Account</span>
              </div>
              <span className="es-bridge-unit">ALL VALUES IN MILLIONS (USD)</span>
            </div>

            {/* Tab toggle — same style as GL Account Budget vs Actuals tabs */}
            <div className="ve-tabs">
              <button
                className={`ve-tab-btn${trendView === "quarterly" ? " ve-tab-btn--active" : ""}`}
                onClick={() => setTrendView("quarterly")}
              >
                Quarterly
              </button>
              <button
                className={`ve-tab-btn${trendView === "monthly" ? " ve-tab-btn--active" : ""}`}
                onClick={() => setTrendView("monthly")}
              >
                Monthly
              </button>
            </div>

            {/* Quarterly Trends by Account */}
            {trendView === "quarterly" && (
              <div style={{ height: 280, paddingTop: 12, opacity: chartRefreshing ? 0.6 : 1, transition: "opacity 0.2s" }}>
                {quarterlyFetching && !quarterlyData ? (
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: "#94a3b8", fontSize: 12 }}>Loading quarterly trends…</div>
                ) : !quarterlyVarianceData?.length ? (
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: "#94a3b8", fontSize: 12 }}>No data available</div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={quarterlyVarianceData} margin={{ top: 10, right: 52, left: 0, bottom: 10 }}>
                      <defs>
                        {quarterlyData.accounts.map((acc: string, i: number) => (
                          <linearGradient key={acc} id={`qgrad-${i}`} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor={CHART_PALETTE[i % CHART_PALETTE.length]} stopOpacity={0.25} />
                            <stop offset="95%" stopColor={CHART_PALETTE[i % CHART_PALETTE.length]} stopOpacity={0.03} />
                          </linearGradient>
                        ))}
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#F0F2F5" vertical={false} />
                      <XAxis dataKey="quarter" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: "#64748b" }} />
                      {/* Left axis: Revenue (high scale) */}
                      <YAxis yAxisId="left" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: "#94a3b8" }} tickFormatter={(v) => `$${v}M`} width={48} />
                      {/* Right axis: All other accounts (auto-scaled to their lower range) */}
                      <YAxis yAxisId="right" orientation="right" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: "#94a3b8" }} tickFormatter={(v) => `$${v}M`} width={48} />
                      <Tooltip
                        formatter={(v: any, name: string | undefined) => [`$${Number(v).toFixed(2)}M`, name ?? ""]}
                        contentStyle={{ borderRadius: 6, border: "1px solid #DEE1E6", fontSize: 11 }}
                      />
                      <Legend wrapperStyle={{ fontSize: 10, paddingTop: 8 }} iconSize={8} iconType="circle" />
                      {quarterlyData.accounts.map((acc: string, i: number) => {
                        const isRevenue = (quarterlyData.accountCategoryMap?.[acc] ?? "EXPENSE") === "REVENUE";
                        return (
                          <Area
                            key={acc}
                            yAxisId={isRevenue ? "left" : "right"}
                            type="monotone"
                            dataKey={acc}
                            stroke={CHART_PALETTE[i % CHART_PALETTE.length]}
                            fill={`url(#qgrad-${i})`}
                            strokeWidth={2}
                            dot={{ r: 3 }}
                            activeDot={{ r: 5 }}
                            connectNulls
                          />
                        );
                      })}
                    </AreaChart>
                  </ResponsiveContainer>
                )}
              </div>
            )}

            {/* Monthly Account Trends */}
            {trendView === "monthly" && (
              <div style={{ height: 280, paddingTop: 12, opacity: chartRefreshing ? 0.6 : 1, transition: "opacity 0.2s" }}>
                {monthlyFetching && !monthlyData ? (
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: "#94a3b8", fontSize: 12 }}>Loading monthly trends…</div>
                ) : !monthlyVarianceData?.length ? (
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: "#94a3b8", fontSize: 12 }}>No monthly data available</div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={monthlyVarianceData} margin={{ top: 10, right: 52, left: 0, bottom: 10 }}>
                      <defs>
                        {monthlyData.accounts.map((acc: string, i: number) => (
                          <linearGradient key={acc} id={`mgrad-${i}`} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor={CHART_PALETTE[i % CHART_PALETTE.length]} stopOpacity={0.25} />
                            <stop offset="95%" stopColor={CHART_PALETTE[i % CHART_PALETTE.length]} stopOpacity={0.03} />
                          </linearGradient>
                        ))}
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#F0F2F5" vertical={false} />
                      <XAxis dataKey="monthLabel" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: "#64748b" }} />
                      {/* Left axis: Revenue (high scale) */}
                      <YAxis yAxisId="left" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: "#94a3b8" }} tickFormatter={(v) => `$${v}M`} width={48} />
                      {/* Right axis: All other accounts (auto-scaled to their lower range) */}
                      <YAxis yAxisId="right" orientation="right" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: "#94a3b8" }} tickFormatter={(v) => `$${v}M`} width={48} />
                      <Tooltip
                        formatter={(v: any, name: string | undefined) => [`$${Number(v).toFixed(2)}M`, name ?? ""]}
                        contentStyle={{ borderRadius: 6, border: "1px solid #DEE1E6", fontSize: 11 }}
                      />
                      <Legend wrapperStyle={{ fontSize: 10, paddingTop: 8 }} iconSize={8} iconType="circle" />
                      {monthlyData.accounts.map((acc: string, i: number) => {
                        const isRevenue = (monthlyData.accountCategoryMap?.[acc] ?? "EXPENSE") === "REVENUE";
                        return (
                          <Area
                            key={acc}
                            yAxisId={isRevenue ? "left" : "right"}
                            type="monotone"
                            dataKey={acc}
                            stroke={CHART_PALETTE[i % CHART_PALETTE.length]}
                            fill={`url(#mgrad-${i})`}
                            strokeWidth={2}
                            dot={{ r: 3 }}
                            activeDot={{ r: 5 }}
                            connectNulls
                          />
                        );
                      })}
                    </AreaChart>
                  </ResponsiveContainer>
                )}
              </div>
            )}
          </div>

          {/* Financial Commentary */}
          <div className="es-commentary-section" style={{ position: "relative" }}>
            <div className="es-commentary-title-row">
              <span className="es-commentary-title">Financial Commentary AI-Draft</span>
            </div>
            <button
              onClick={handleGenerateAI}
              disabled={aiLoading}
              style={{
                position: "absolute",
                top: 10,
                right: 10,
                margin: 10,
                padding: "6px 14px",
                fontSize: 11,
                fontWeight: 600,
                borderRadius: 50,
                border: "none",
                background: aiLoading
                  ? "linear-gradient(135deg, #4a8fd4, #5ba3e8)"
                  : "linear-gradient(135deg, #1565c0, #2590F4)",
                color: "#fff",
                cursor: aiLoading ? "not-allowed" : "pointer",
                display: "flex",
                alignItems: "center",
                gap: 6,
                opacity: aiLoading ? 0.8 : 1,
                boxShadow: "0 2px 8px rgba(21,101,192,0.3)",
                transition: "opacity 0.15s, box-shadow 0.15s",
              }}
            >
              {aiLoading ? (
                <>
                  <span style={{ display: "inline-block", width: 10, height: 10, border: "2px solid rgba(255,255,255,0.5)", borderTopColor: "#fff", borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />
                  Generating…
                </>
              ) : aiCommentary ? (
                <>↺ Regenerate</>
              ) : (
                <>✦ Generate AI Commentary</>
              )}
            </button>

            {aiError && (
              <div style={{ color: "#dd3c57", fontSize: 11, padding: "4px 0" }}>{aiError}</div>
            )}

            <div className="es-commentary-body">
              {aiLoading ? (
                <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "20px 0", color: "#94a3b8", fontSize: 12 }}>
                  <span style={{ display: "inline-block", width: 14, height: 14, border: "2px solid #165697", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />
                  Generating AI commentary…
                </div>
              ) : aiCommentary ? (
                <div>
                  <div style={{ fontSize: 11, color: "#64748b", marginBottom: 8, display: "flex", alignItems: "center", gap: 4 }}>
                    <span style={{ background: "#165697", color: "#fff", fontSize: 9, fontWeight: 700, padding: "1px 5px", borderRadius: 3 }}>AI</span>
                    Generated based on applied filters: FY{filters?.fiscalYear} · {filters?.accountCategory ?? "All"} · {filters?.costCenter ?? "All CCs"}
                  </div>
                  <div
                    style={{ fontSize: 12.5, color: "#334155", lineHeight: 1.7 }}
                    dangerouslySetInnerHTML={{ __html: aiCommentary }}
                  />
                </div>
              ) : (
                <div style={{ color: "#94a3b8", fontSize: 12, fontStyle: "italic", padding: "16px 0" }}>
                  Click "Generate AI Commentary" to get an AI-powered analysis based on the current filters.
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* ── Right Panel ───────────────────────────────────────── */}
      <aside className="es-right-panel">
        <div className="es-panel-header">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <rect x="1" y="1" width="5" height="5" rx="1" stroke="#165697" strokeWidth="1.2" />
            <rect x="8" y="1" width="5" height="5" rx="1" stroke="#165697" strokeWidth="1.2" />
            <rect x="1" y="8" width="5" height="5" rx="1" stroke="#165697" strokeWidth="1.2" />
            <rect x="8" y="8" width="5" height="5" rx="1" stroke="#165697" strokeWidth="1.2" />
          </svg>
          <span className="es-panel-header-text">Management Actions</span>
        </div>

        <div className="es-actions-list">
          <div className="es-action-card">
            <div className="es-action-icon">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <rect x="2.5" y="2.5" width="11" height="11" rx="1.5" stroke="#165697" strokeWidth="1.2" />
                <path d="M5 8.5h6M5 6h4M5 11h3" stroke="#165697" strokeWidth="1.2" strokeLinecap="round" />
              </svg>
            </div>
            <div className="es-action-content">
              <div className="es-action-title">Save Current View</div>
              <div className="es-action-desc">Persist this selection to your private dashboard</div>
            </div>
          </div>

          <div className="es-action-card" style={{ cursor: "pointer" }} onClick={handleExportPdf}>
            <div className="es-action-icon">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M10 2H4a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V5l-3-3z" stroke="#165697" strokeWidth="1.2" />
                <path d="M10 2v3h3M5.5 9.5h5M5.5 7.5h3" stroke="#165697" strokeWidth="1.2" strokeLinecap="round" />
              </svg>
            </div>
            <div className="es-action-content">
              <div className="es-action-title">Export Summary</div>
              <div className="es-action-desc">
                {exporting ? "Generating PDF…" : "Generate PDF and PPTX briefing decks"}
              </div>
            </div>
          </div>

          <div className="es-action-card es-action-card--primary">
            <div className="es-action-icon es-action-icon--white">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <circle cx="4"  cy="8"  r="1.5" stroke="white" strokeWidth="1.2" />
                <circle cx="12" cy="4"  r="1.5" stroke="white" strokeWidth="1.2" />
                <circle cx="12" cy="12" r="1.5" stroke="white" strokeWidth="1.2" />
                <path d="M5.5 7.2L10.5 4.8M5.5 8.8L10.5 11.2" stroke="white" strokeWidth="1.2" />
              </svg>
            </div>
            <div className="es-action-content">
              <div className="es-action-title es-action-title--white">Share with Leadership</div>
              <div className="es-action-desc es-action-desc--white">Distribute to Finance Leadership</div>
            </div>
          </div>
        </div>

        <div className="es-governance-section">
          <div className="es-governance-header">Governance &amp; Compliance</div>

          <div className="es-governance-item">
            <div className="es-gov-icon">
              <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
                <path d="M7.5 1.5L2 4v4c0 3.2 2.5 5.5 5.5 6.3 3-0.8 5.5-3.1 5.5-6.3V4L7.5 1.5z" stroke="#165697" strokeWidth="1.2" strokeLinejoin="round" />
                <path d="M5 7.5l1.8 1.8 3.2-3.2" stroke="#165697" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <div className="es-gov-content">
              <div className="es-gov-title">Role-Based Security</div>
              <div className="es-gov-desc">
                Access restricted to Tier-1 Finance Executives and authorized auditors.
              </div>
            </div>
          </div>

          <div className="es-governance-item">
            <div className="es-gov-icon">
              <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
                <path d="M2 11L5.5 6.5l3 3L13 3" stroke="#165697" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <div className="es-gov-content">
              <div className="es-gov-title">Activity Auditing</div>
              <div className="es-gov-desc">
                All view exports and sharing actions are logged with timestamped IP tracking.
              </div>
            </div>
          </div>
        </div>

        <div className="es-right-footer">
          <div className="es-right-footer-top">Enterprise Data Governance V2.4</div>
          <div className="es-right-footer-copy">© 2025 ExecutivLens Financial Intelligence</div>
        </div>
      </aside>

      {/* Spinner keyframe */}
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default ExecutiveSummary;
